import { useState, useEffect } from 'react'
import PhoneLogin from './components/Auth/PhoneLogin'
import VerificationCode from './components/Auth/VerificationCode'
import DataView from './components/Data/DataView'
import { LanguageProvider } from './context/LanguageContext'
import { requestOtp, verifyOtp, validatePhoneNumber, isUsingMockData } from './services/apiService'

// Family data persistence helpers
const FAMILY_DATA_STORAGE_KEY = 'kidsParentsApp_familyData'
const VIEW_MODE_STORAGE_KEY = 'kidsParentsApp_viewMode'

const saveFamilyData = (familySession) => {
  try {
    localStorage.setItem(FAMILY_DATA_STORAGE_KEY, JSON.stringify(familySession))
  } catch (error) {
    console.warn('Failed to save family data:', error)
  }
}

const loadFamilyData = () => {
  try {
    const stored = localStorage.getItem(FAMILY_DATA_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (error) {
    console.warn('Failed to load family data:', error)
    return null
  }
}

const clearFamilyData = () => {
  try {
    localStorage.removeItem(FAMILY_DATA_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear family data:', error)
  }
}

const clearViewMode = () => {
  try {
    localStorage.removeItem(VIEW_MODE_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear view mode:', error)
  }
}

function App() {
  const [currentPage, setCurrentPage] = useState('login') // 'login', 'verification', 'data'
  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationMethod, setVerificationMethod] = useState('')
  const [familyData, setFamilyData] = useState(null)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)

  // Restore family data on app startup
  useEffect(() => {
    const savedFamilyData = loadFamilyData()
    if (savedFamilyData && savedFamilyData.familyData && savedFamilyData.phoneNumber) {
      // Check if session is expired (1 hour)
      const isExpired = savedFamilyData.timestamp && (Date.now() - savedFamilyData.timestamp) > (1 * 60 * 60 * 1000)
      
      if (isExpired) {
        clearFamilyData()
        console.log('Stored family data session expired')
      } else {
        setCurrentPage('data')
        setPhoneNumber(savedFamilyData.phoneNumber)
        setFamilyData(savedFamilyData.familyData)
        console.log('Restored family data from storage')
      }
    }
    setIsInitializing(false)
  }, [])

  // Periodic session expiration check (every 5 minutes)
  useEffect(() => {
    const checkSessionExpiration = () => {
      if (currentPage === 'data') {
        const savedFamilyData = loadFamilyData()
        if (savedFamilyData && savedFamilyData.timestamp) {
          const timeElapsed = Date.now() - savedFamilyData.timestamp
          const isExpired = timeElapsed > (1 * 60 * 60 * 1000) // 1 hour
          
          if (isExpired) {
            console.log('Session expired - automatically signing out')
            handleBackToLogin()
          }
        }
      }
    }

    // Check immediately
    checkSessionExpiration()
    
    // Set up interval to check every 5 minutes
    const intervalId = setInterval(checkSessionExpiration, 5 * 60 * 1000)

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId)
  }, [currentPage])

  // Check session expiration when user focuses back to the app
  useEffect(() => {
    const handleFocus = () => {
      if (currentPage === 'data') {
        const savedFamilyData = loadFamilyData()
        if (savedFamilyData && savedFamilyData.timestamp) {
          const timeElapsed = Date.now() - savedFamilyData.timestamp
          const isExpired = timeElapsed > (1 * 60 * 60 * 1000) // 1 hour
          if (isExpired) {
            console.log('Session expired (detected on focus) - automatically signing out')
            handleBackToLogin()
          }
        }
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [currentPage])

  const handlePhoneSubmit = async (phone, method) => {
    setIsLoading(true)
    setError('')
    
    try {
      const result = await requestOtp(phone, method)
      
      if (result.success) {
        setPhoneNumber(phone)
        setVerificationMethod(method)
        setCurrentPage('verification')
      } else {
        setError(result.data.error || 'Phone number not found. Please check your number or contact Ruslan Matev if you believe this is correct.')
      }
    } catch (error) {
      console.error('Error requesting OTP:', error)
      setError('Network error. Please check your connection and try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerificationSubmit = async (code) => {
    setIsLoading(true)
    setError('')
    
    try {
      const result = await verifyOtp(phoneNumber, code)
      
      if (result.success) {
        const familyData = result.data.familyData
        setFamilyData(familyData)
        setCurrentPage('data')
        
        // Save family data to localStorage
        saveFamilyData({
          phoneNumber,
          familyData,
          timestamp: Date.now()
        })
      } else {
        setError(result.data.error || 'Invalid verification code. Please try again.')
      }
    } catch (error) {
      console.error('Error verifying OTP:', error)
      setError('Invalid verification code. Please try again.')//!!!!!!Not the proper placement.
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setCurrentPage('login')
    setPhoneNumber('')
    setVerificationMethod('')
    setFamilyData(null)
    setError('')
    setIsLoading(false)
    
    // Clear stored family data and view mode
    clearFamilyData()
    clearViewMode()
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'login':
        return <PhoneLogin onSubmit={handlePhoneSubmit} error={error} isLoading={isLoading} />
      case 'verification':
        return (
          <VerificationCode 
            phoneNumber={phoneNumber}
            verificationMethod={verificationMethod}
            onSubmit={handleVerificationSubmit}
            onBack={handleBackToLogin}
            error={error}
            isLoading={isLoading}
          />
        )
      case 'data':
        return <DataView phoneNumber={phoneNumber} familyData={familyData} onBack={handleBackToLogin} />
      default:
        return <PhoneLogin onSubmit={handlePhoneSubmit} error={error} isLoading={isLoading} />
    }
  }

  return (
    <LanguageProvider>
      <div className={`app ${currentPage === 'data' ? 'app-data-view' : 'app-auth-view'}`}>
        {isInitializing ? (
          <div className="app-loading">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          renderCurrentPage()
        )}
      </div>
    </LanguageProvider>
  )
}

export default App

